/**
 * The collage's arc flourishes, exported from the design's variations board
 * (Figma 14326:80204) and recoloured to `currentColor` — the board's own files
 * disagree on the stroke (some teal, some purple), and the UX guide is explicit
 * that the lines take the restaurant's primary colour.
 *
 * There are two motifs, not one: a tall pair on the leading side and a wider
 * pair on the trailing side. The board turns each of them, and by a different
 * amount once the grid reaches six, so the turn is baked into the artwork here
 * rather than applied in CSS. That keeps the layout's numbers equal to the
 * board's own layer bounds, and leaves no rotation whose direction can be read
 * the wrong way round.
 *
 * Each viewBox is the artwork's turned box in board pixels, so drawing it at
 * that size puts its 1-unit stroke on one board pixel; the collage's canvas
 * then scales the whole thing.
 *
 * `preserveAspectRatio="none"` matches how Figma itself fills these nodes.
 */
export type CollageArcShape =
  | "start"
  | "startEcho"
  | "startPair"
  | "startPairTurned"
  | "end"
  | "endEcho"
  | "endPair"
  | "endPairTurned";

/** Figma "Path 7" — the long leading arc, drawn alone for 1-2 images. */
const Start = () => (
  <svg
    viewBox="0 0 17.7015 57.6750"
    fill="none"
    preserveAspectRatio="none"
    aria-hidden="true"
  >
    <g>
      <path
        d="M0.5 0C0.5 22.7339 2.78069 38.6895 17.3068 57.3681"
        stroke="currentColor"
      />
    </g>
  </svg>
);

/** Figma "Path 8" — the short arc echoing the leading one. */
const StartEcho = () => (
  <svg
    viewBox="0 0 16.6928 24.2743"
    fill="none"
    preserveAspectRatio="none"
    aria-hidden="true"
  >
    <g>
      <path
        d="M0.492324 0.0872739C3.01427 14.3139 16.3541 24.2352 16.1913 23.7578"
        stroke="currentColor"
      />
    </g>
  </svg>
);

/** Figma "Path 12" — the long trailing arc, drawn alone for 1-2 images. */
const End = () => (
  <svg
    viewBox="0 0 50.2054 46.4205"
    fill="none"
    preserveAspectRatio="none"
    aria-hidden="true"
  >
    <g>
      <path
        d="M0.0686868 0.49526C23.361 3.72563 43.759 22.5394 49.7205 46.2988"
        stroke="currentColor"
      />
    </g>
  </svg>
);

/** Figma "Path 12 Copy" — the short trailing echo, which the board turns 10 degrees. */
const EndEcho = () => (
  <svg
    viewBox="0 0 21.3020 12.4828"
    fill="none"
    preserveAspectRatio="none"
    aria-hidden="true"
  >
    <g transform="translate(10.6510 6.2414) rotate(-10.0) translate(-10.0090 -4.5728)">
      <path
        d="M0.0686868 0.49526C7.17642 1.48102 13.9229 4.41209 19.7186 8.74523"
        stroke="currentColor"
      />
    </g>
  </svg>
);

/** Figma "lines" — the leading pair as the board groups them for 3-5 images. */
const StartPair = () => (
  <svg
    viewBox="0 0 74.7979 78.4166"
    fill="none"
    preserveAspectRatio="none"
    aria-hidden="true"
  >
    <g
      strokeLinecap="round"
      transform="translate(37.3990 39.2083) rotate(-42.2) translate(-14.0220 -40.2122)"
    >
      <path
        d="M4.83179 0.5C4.83179 31.2218 7.91384 52.7838 27.544 78.0254"
        stroke="currentColor"
      />
      <path
        d="M0.500084 47.9146C3.90815 67.14 21.9351 80.5473 21.7151 79.9022"
        stroke="currentColor"
      />
    </g>
  </svg>
);

/** The same leading pair, turned as the board turns it for 6-9 images. */
const StartPairTurned = () => (
  <svg
    viewBox="0 0 83.0319 66.4942"
    fill="none"
    preserveAspectRatio="none"
    aria-hidden="true"
  >
    <g
      strokeLinecap="round"
      transform="translate(41.5159 33.2471) rotate(-57.9) translate(-14.0220 -40.2122)"
    >
      <path
        d="M4.83179 0.5C4.83179 31.2218 7.91384 52.7838 27.544 78.0254"
        stroke="currentColor"
      />
      <path
        d="M0.500084 47.9146C3.90815 67.14 21.9351 80.5473 21.7151 79.9022"
        stroke="currentColor"
      />
    </g>
  </svg>
);

/** Figma "Lines 2" — the trailing pair as the board groups them for 3-5 images. The board fills its echo rather than stroking it. */
const EndPair = () => (
  <svg
    viewBox="0 0 67.6511 72.7868"
    fill="none"
    preserveAspectRatio="none"
    aria-hidden="true"
  >
    <g>
      <path
        d="M0.0686876 10.7674C31.5449 15.1328 59.11 40.5572 67.1661 72.6651"
        stroke="currentColor"
      />
      <path
        d="M0.646812 4.61111L0.665168 5.11078C10.264 4.75811 19.8468 7.05374 28.5081 11.4258L28.7334 10.9794L28.9587 10.5331C20.1601 6.09171 10.4106 3.75205 0.628456 4.11145L0.646812 4.61111Z"
        fill="currentColor"
      />
    </g>
  </svg>
);

/** The same trailing pair, turned as the board turns it for 6-9 images. */
const EndPairTurned = () => (
  <svg
    viewBox="0 0 85.3607 88.8486"
    fill="none"
    preserveAspectRatio="none"
    aria-hidden="true"
  >
    <g transform="translate(42.6804 44.4243) rotate(-16.3) translate(-33.8255 -36.3934)">
      <path
        d="M0.0686876 10.7674C31.5449 15.1328 59.11 40.5572 67.1661 72.6651"
        stroke="currentColor"
      />
      <path
        d="M0.646812 4.61111L0.665168 5.11078C10.264 4.75811 19.8468 7.05374 28.5081 11.4258L28.7334 10.9794L28.9587 10.5331C20.1601 6.09171 10.4106 3.75205 0.628456 4.11145L0.646812 4.61111Z"
        fill="currentColor"
      />
    </g>
  </svg>
);

const SHAPES: Record<CollageArcShape, () => JSX.Element> = {
  start: Start,
  startEcho: StartEcho,
  startPair: StartPair,
  startPairTurned: StartPairTurned,
  end: End,
  endEcho: EndEcho,
  endPair: EndPair,
  endPairTurned: EndPairTurned,
};

export const CollageArc = ({ shape }: { shape: CollageArcShape }) => {
  const Shape = SHAPES[shape];

  return <Shape />;
};
